/**
 * Erwal — winkelwagenregels samenvoegen.
 *
 * Bij een staffelkorting ("3 voor € 12,50") verdeelt Shopify de regel in stuks
 * met en zonder korting. De klant ziet dan twee regels van hetzelfde product met
 * verschillende prijzen. Dit script voegt die samen tot één regel met het juiste
 * totaal en één aantalkiezer.
 *
 * Belangrijk: dit draait BOVENOP Horizon en verandert geen enkel themabestand.
 * Herkent het de winkelwagen niet meer na een update, dan doet het niets en valt
 * alles terug op de weergave van Shopify zelf. Nooit een kapotte winkelwagen.
 *
 * Aantal wijzigen loopt via Horizons eigen gebeurtenis, zodat de aanroep naar
 * Shopify, de foutafhandeling en het opnieuw opbouwen van de sectie bij Horizon
 * blijven. Wij vertalen alleen het gewenste totaal naar de juiste regel.
 */
(() => {
  const ROW = '.cart-items__table-row';
  const EVT = 'quantity-selector:update';
  const MARK = 'data-erwal-merged';

  const root = () => window.Shopify?.routes?.root || '/';
  const money = (cents) => '€' + (cents / 100).toFixed(2).replace('.', ',');

  /** Regelsleutel is "variantid:hash". */
  const variantOf = (key) => String(key || '').split(':')[0];

  const fetchCart = async () => {
    try {
      const res = await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } });
      return res.ok ? await res.json() : null;
    } catch {
      return null;
    }
  };

  /** Groepen van meer dan één regel met dezelfde variant. */
  const groupRows = (scope) => {
    const groups = new Map();
    scope.querySelectorAll(`${ROW}[data-key]`).forEach((row) => {
      if (row.dataset.parentKey) return; // bundelonderdelen laat Shopify bewust apart zien
      const id = variantOf(row.dataset.key);
      if (!id) return;
      if (!groups.has(id)) groups.set(id, []);
      groups.get(id).push(row);
    });
    return [...groups.values()].filter((rows) => rows.length > 1);
  };

  const merge = (rows, cart) => {
    const primary = rows[0];
    if (primary.hasAttribute(MARK)) return;

    // Bedragen uit de winkelwagen zelf halen, niet uit de tekst op het scherm.
    const keys = rows.map((r) => r.dataset.key);
    const lines = keys.map((k) => (cart.items || []).find((i) => i.key === k));
    if (lines.some((l) => !l)) return;

    const totalQty = lines.reduce((n, l) => n + l.quantity, 0);
    const totalFinal = lines.reduce((n, l) => n + l.final_line_price, 0);
    const totalOriginal = lines.reduce((n, l) => n + l.original_line_price, 0);
    if (!totalQty) return;

    primary.setAttribute(MARK, '');
    primary.dataset.erwalOwnQty = String(lines[0].quantity);
    primary.dataset.erwalTotalQty = String(totalQty);
    primary.dataset.erwalVariant = String(lines[0].variant_id);

    // De overige regels verdwijnen; hun aantal zit nu in de eerste regel.
    rows.slice(1).forEach((r) => {
      r.setAttribute('hidden', '');
      r.setAttribute('aria-hidden', 'true');
    });

    const input = primary.querySelector('input[name="updates[]"]');
    if (input) {
      input.value = String(totalQty);
      input.dataset.cartQuantity = String(totalQty);
    }

    // Regeltotaal: gekort bedrag in de text-component, origineel doorgestreept erboven
    // (zelfde opbouw als cart-products.liquid, zodat de CSS voor beide geldt).
    const priceCell = primary.querySelector('.cart-items__price');
    if (priceCell) {
      const nu = priceCell.querySelector('.erwal-regeltotaal-nu, text-component');
      if (nu) {
        nu.textContent = money(totalFinal);
        nu.setAttribute('value', money(totalFinal));
      }
      let was = priceCell.querySelector('.erwal-regeltotaal-was');
      if (totalFinal !== totalOriginal) {
        if (!was) {
          was = document.createElement('s');
          was.className = 'erwal-regeltotaal-was';
          priceCell.prepend(was);
        }
        was.textContent = money(totalOriginal);
        priceCell.classList.add('cart-items__price--korting');
      } else if (was) {
        was.remove();
        priceCell.classList.remove('cart-items__price--korting');
      }
    }

    // Stukprijs als gemiddelde over alle stuks; de doorstreping staat rechts op het totaal.
    const unitWrap = primary.querySelector('.cart-items__unit-price-wrapper');
    if (unitWrap && totalFinal !== totalOriginal) {
      unitWrap.innerHTML =
        `<span class="erwal-unit">${money(Math.round(totalFinal / totalQty))}</span> ` +
        `<span class="erwal-perstuk">per stuk</span>`;
    }
  };

  /**
   * Het invoerveld toont het totaal, maar Horizon stuurt de waarde naar één
   * regel van Shopify. Hier vertalen we het gewenste totaal naar het verschil
   * op die eigen regel; Shopify verdeelt de korting daarna zelf opnieuw.
   */
  const onQuantityEvent = (event) => {
    if (event.__erwal) return;
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const row = target.closest(`${ROW}[${MARK}]`);
    if (!row) return;

    const ownQty = parseInt(row.dataset.erwalOwnQty || '0', 10);
    const totalQty = parseInt(row.dataset.erwalTotalQty || '0', 10);
    const wanted = Number(event.detail?.quantity);
    if (!Number.isFinite(wanted) || !totalQty) return;

    const next = Math.max(0, ownQty + (wanted - totalQty));
    if (next === wanted) return; // niets te vertalen

    event.stopImmediatePropagation();

    const corrected = new Event(EVT, { bubbles: true });
    corrected.detail = { quantity: next, cartLine: event.detail?.cartLine };
    corrected.__erwal = true;
    target.dispatchEvent(corrected);
  };

  /** Sectie opnieuw ophalen na een wijziging die buiten Horizon om ging. */
  const refresh = async (scope) => {
    const id = scope.dataset.sectionId;
    if (!id) return window.location.reload();
    try {
      const res = await fetch(`${window.location.pathname}?section_id=${encodeURIComponent(id)}`);
      if (!res.ok) throw new Error('sectie niet opgehaald');
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const fresh = doc.querySelector(`[data-section-id="${id}"]`);
      const current = document.querySelector(`[data-section-id="${id}"]`);
      if (!fresh || !current) throw new Error('sectie niet gevonden');
      current.replaceWith(fresh);
      run();
    } catch {
      window.location.reload();
    }
  };

  /**
   * Verwijderen moet alle regels van de variant meenemen, niet alleen de eerste.
   * Dat kan Horizon niet weten, dus die ene handeling doen we zelf.
   */
  const onRemoveClick = (event) => {
    const button = event.target instanceof HTMLElement && event.target.closest('.cart-items__remove');
    if (!button) return;
    const row = button.closest(`${ROW}[${MARK}]`);
    if (!row) return;
    const variantId = row.dataset.erwalVariant;
    const scope = row.closest('cart-items-component');
    if (!variantId || !scope) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    fetch(`${root()}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: { [variantId]: 0 } }),
    })
      .then((res) => {
        if (!res.ok) throw new Error('bijwerken mislukt');
        return refresh(scope);
      })
      .catch(() => window.location.reload());
  };

  /**
   * "Je bespaart" in het totaaloverzicht. Horizon toont alleen kortingen op
   * bestelniveau; onze staffelkortingen zitten op regelniveau en bleven daardoor
   * onzichtbaar. Het verschil tussen de oorspronkelijke en de actuele prijs dekt
   * beide soorten korting.
   */
  const showSavings = (cart) => {
    const saved = (cart.original_total_price || 0) - (cart.total_price || 0);

    document.querySelectorAll('.cart-totals').forEach((totals) => {
      const existing = totals.querySelector('.erwal-savings');
      if (saved <= 0) {
        existing?.remove();
        return;
      }
      const text = `Je bespaart ${money(saved)}`;
      if (existing) {
        existing.querySelector('.erwal-savings__value').textContent = money(saved);
        return;
      }
      const row = document.createElement('div');
      row.className = 'erwal-savings';
      row.innerHTML =
        '<span class="erwal-savings__label">Je bespaart</span>' +
        `<span class="erwal-savings__value">${money(saved)}</span>`;
      row.setAttribute('aria-label', text);
      totals.prepend(row);
    });
  };

  const run = async () => {
    const scopes = document.querySelectorAll('cart-items-component');
    if (!scopes.length) return;

    const cart = await fetchCart();
    if (!cart) return;

    for (const scope of scopes) {
      groupRows(scope).forEach((rows) => merge(rows, cart));
    }
    showSavings(cart);
  };

  const boot = () => {
    // Beide in de doorloopfase, zodat we vóór Horizon aan de beurt zijn.
    document.addEventListener(EVT, onQuantityEvent, true);
    document.addEventListener('click', onRemoveClick, true);

    // Horizon bouwt de sectie opnieuw op na elke wijziging; dan opnieuw draaien.
    new MutationObserver((records) => {
      const touched = records.some((r) =>
        [...r.addedNodes].some(
          (n) => n instanceof HTMLElement && (n.matches?.(ROW) || n.querySelector?.(ROW))
        )
      );
      if (touched) run();
    }).observe(document.body, { childList: true, subtree: true });

    run();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
